/**
 * Single source of truth for the Tennis Log user-facing password policy.
 *
 * Policy: minimum 8 characters, no mandatory uppercase / numbers / symbols.
 * Leaked & common password protection is enforced by the backend (HIBP),
 * whose own minimum is lower (6) — the app always enforces the stricter 8.
 */
export const MIN_PASSWORD_LENGTH = 8;

type Translate = (key: string, options?: Record<string, unknown>) => string;

type PasswordErrorLike = {
  message?: string;
  code?: string;
  weak_password?: { reasons?: string[] };
};

/**
 * Maps a backend password error to localized copy.
 * Returns null when the error is not password-policy related.
 * Raw backend English messages are never returned.
 */
export function mapPasswordError(error: unknown, t: Translate): string | null {
  const err = (error ?? {}) as PasswordErrorLike;
  const code = err.code ?? "";
  const reasons = err.weak_password?.reasons ?? [];
  const raw = (err.message || "").toLowerCase();

  if (code !== "weak_password" && !raw.includes("password")) return null;

  const isLength = reasons.includes("length") || raw.includes("at least");
  const isPwned = reasons.includes("pwned") || raw.includes("known to be weak");

  if (isLength) {
    return t("auth.passwordTooShort", {
      defaultValue: `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`,
    });
  }
  if (isPwned) {
    return t("auth.passwordWeakLeaked", {
      defaultValue:
        "This password is too common or has appeared in a known data breach. Try a more unique password.",
    });
  }
  return t("auth.passwordRejected", {
    defaultValue:
      "This password wasn't accepted. Check the requirements next to the field and try again.",
  });
}

export function passwordTooShortMessage(t: Translate): string {
  return t("auth.passwordTooShort", {
    defaultValue: `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`,
  });
}
